//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import DropdownSelect from '@components/molecules/dropdown-select/DropdownSelect';
import { InfluencersTrackingIds } from '@influencers/constants/influencersConstants';
import { IInfluencersUser } from '@influencers/models/influencersStateModel';

export enum CreatorListSort {
  FOLLOWERS = 'Most Followers',
  POSTS_FT_BRAND = 'Most Posts ft. My Brand',
  FOLLOWER_ENG_RATE = 'Highest Follower Eng. Rate',
  AVG_EMV = 'Highest Avg. EMV',
}

const ORDERED_SORT_OPTIONS = [
  CreatorListSort.FOLLOWERS,
  CreatorListSort.POSTS_FT_BRAND,
  CreatorListSort.FOLLOWER_ENG_RATE,
  CreatorListSort.AVG_EMV,
] as const;

const items = ORDERED_SORT_OPTIONS.map((sortKey) => {
  return {
    value: sortKey,
    label: sortKey,
  };
});

export const sortUsers = (
  sortType: CreatorListSort,
  allUsers: IInfluencersUser[],
): IInfluencersUser[] => {
  switch (sortType) {
    case CreatorListSort.FOLLOWERS:
      return [...allUsers].sort((a, b) => b.followersCount - a.followersCount);
    case CreatorListSort.POSTS_FT_BRAND:
      return [...allUsers].sort(
        (a, b) => (b.metrics.postsFtBrand ?? 0) - (a.metrics.postsFtBrand ?? 0),
      );
    case CreatorListSort.FOLLOWER_ENG_RATE:
      return [...allUsers].sort((a, b) => b.engagementRate - a.engagementRate);
    case CreatorListSort.AVG_EMV:
      return [...allUsers].sort(
        (a, b) => (b.metrics.averageEMV ?? 0) - (a.metrics.averageEMV ?? 0),
      );
    default:
      return [...allUsers].sort((a, b) => b.username.localeCompare(a.username));
  }
};

interface Props {
  isEmptyCreatorList: boolean;
  isLoading: boolean;
  sortType: CreatorListSort;
  setSortType: (sortType: CreatorListSort) => void;
}

export const CreatorTableSort = ({
  isEmptyCreatorList,
  isLoading,
  sortType,
  setSortType,
}: Props) => {
  return (
    <SortDropdownContainer>
      <DropdownSelect
        handleSelect={(value) => setSortType(value)}
        isDisabled={isLoading || isEmptyCreatorList}
        items={items}
        trackingId={InfluencersTrackingIds.CREATOR_LISTS_USERS_SORT_DROPDOWN}
        value={sortType}
      />
    </SortDropdownContainer>
  );
};

const SortDropdownContainer = styled.div(
  ({ theme }) => `
    min-width: ${theme.smallMeasure};
  `,
);
